import { useEffect, useState } from 'react';

type IdleCallbackHandle = number;
type IdleCallbackDeadline = { didTimeout: boolean; timeRemaining: () => number };
type IdleCallback = (callback: (deadline: IdleCallbackDeadline) => void, options?: { timeout: number }) => IdleCallbackHandle;
type IdleCancel = (handle: IdleCallbackHandle) => void;

declare global {
  interface Window {
    requestIdleCallback?: IdleCallback;
    cancelIdleCallback?: IdleCancel;
  }
}

export function useDeferredActivation(enabled: boolean, timeoutMs = 250) {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsActive(false);
      return;
    }

    let timeoutHandle: number | null = null;
    let idleHandle: number | null = null;

    const activate = () => setIsActive(true);

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(() => activate(), { timeout: timeoutMs });
    } else {
      timeoutHandle = window.setTimeout(activate, timeoutMs);
    }

    return () => {
      if (idleHandle !== null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }

      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [enabled, timeoutMs]);

  return isActive;
}
